import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useFavoris } from '../context/FavorisContext';
import ProduitCard from '../components/ProduitCard';
import TraditionalPattern from '../components/TraditionalPattern';

const Favoris = () => {
  const { favoris, totalFavoris } = useFavoris();
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorisProduits = async () => {
      if (favoris.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Récupérer les détails de chaque produit favori
        const produitsPromises = favoris.map(produitId =>
          axios.get(`/produits/${produitId}`).catch(() => null)
        );
        
        const produitsResponses = await Promise.all(produitsPromises);
        const produitsData = produitsResponses
          .filter(res => res !== null)
          .map(res => res.data);
        
        setProduits(produitsData);
      } catch (error) {
        console.error('Erreur lors du chargement des favoris:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorisProduits();
  }, [favoris]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative transition-colors duration-300">
      <TraditionalPattern variant="dots" opacity={0.04} className="text-primary-600 dark:text-primary-400 fixed inset-0" />
      
      <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-pink-600 dark:from-red-600 dark:to-pink-700 mb-4 shadow-lg">
            <i className="fas fa-heart text-white text-3xl"></i>
          </div>
          <h1 className="font-display text-4xl font-bold text-primary-900 dark:text-primary-300 mb-4">
            Mes Favoris
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            {totalFavoris > 0 ? (
              <>
                Vous avez <span className="font-semibold text-red-500 dark:text-red-400">{totalFavoris}</span> produit{totalFavoris > 1 ? 's' : ''} en favoris
              </>
            ) : (
              'Aucun produit en favoris pour le moment'
            )}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="fas fa-spinner fa-spin text-5xl text-red-500 dark:text-red-400 mb-4"></i>
            <p className="text-gray-600 dark:text-gray-300 text-lg">Chargement de vos favoris...</p>
          </div>
        ) : produits.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
            {produits.map((produit) => (
              <ProduitCard 
                key={produit._id} 
                produit={produit} 
                onRemoveFromFavoris={() => {
                  // Cette fonction sera appelée après suppression pour mettre à jour la liste
                  setProduits(prev => prev.filter(p => p._id !== produit._id));
                }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-32 h-32 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
              <i className="far fa-heart text-6xl text-gray-300 dark:text-gray-600"></i>
            </div>
            <h3 className="font-display text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">
              Aucun favori pour le moment
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md">
              Parcourez nos produits et ajoutez-les à vos favoris en cliquant sur l'icône cœur
            </p>
            <Link 
              to="/produits" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-600 dark:from-red-600 dark:to-pink-700 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              <i className="fas fa-shopping-bag"></i>
              Découvrir les produits
            </Link>
          </div>
        )}

        {/* Info Card */}
        {produits.length > 0 && (
          <div className="mt-12 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-2xl p-8 border-2 border-red-200 dark:border-red-700">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <i className="fas fa-info-circle text-3xl text-red-500 dark:text-red-400"></i>
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  À propos de vos favoris
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Vos favoris sont sauvegardés localement sur cet appareil. 
                  {/* Si vous êtes connecté, ils sont également synchronisés avec votre compte. */}
                  Pour commander un produit, cliquez sur le bouton WhatsApp sur la fiche produit.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Favoris;
